import { Box, Button, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/system';
import { formatInTimeZone } from 'date-fns-tz';
import de from 'date-fns/locale/de';

import { getFormattedDateString } from '@widget/toolbox/utils';
import { getUserTimeZone } from '@widget/utils/getUserTimezone';
import { Icon } from '@toolbox/components';
import { ConfirmationIcon } from '@toolbox/components/HeaderCommon/CallBackBlock/components/ConfirmationIcon';

import type { StepCommonType } from '@toolbox/components/HeaderCommon/CallBackBlock/types';
import type { FC } from 'react';

export const Row = styled('div')({
  display: 'grid',
  gridTemplateColumns: '24px 1fr',
  alignItems: 'center',
  gridGap: '16px',
  marginBottom: '20px',
  fontSize: '18px',
  fontWeight: 400,
});

export type ConfirmationStepProps = StepCommonType & {
  confirmationTitle?: string;
  confirmationDescription?: string;
  closeCtaLabel?: string;
  addToCalendarCtaLabel?: string;
  addToCalendarTitle?: string;
};
export const ConfirmationStep: FC<ConfirmationStepProps> = ({
  data,
  closeDialog,
  confirmationTitle,
  confirmationDescription,
  closeCtaLabel,
  addToCalendarCtaLabel,
  addToCalendarTitle,
}) => {
  const theme = useTheme();
  const timezone = getUserTimeZone();

  const bookDate = data.salesStartTime
    ? formatInTimeZone(data.salesStartTime, timezone, 'EEEE, dd MMMM', { locale: de })
    : '';
  const bookTime = data.salesStartTime
    ? formatInTimeZone(data.salesStartTime, timezone, 'HH:mm (zzz)', {
        locale: de,
      })
    : '';

  const formattedDate = getFormattedDateString(data.salesStartTime);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflow: 'auto' }}>
      <Box
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          color: theme.palette.primary.main,
          alignItems: 'center',
          overflow: 'auto',
          padding: '16px 32px',
          [theme.breakpoints.down('sm')]: {
            padding: '16px',
          },
        }}
      >
        <Box
          sx={{
            width: '140px',
            height: '140px',
            [theme.breakpoints.down('lg')]: {
              width: '68px',
              height: '68px',
            },
          }}
        >
          <ConfirmationIcon />
        </Box>
        <Typography variant={'h4'} my={{ sm: '16px', md: '24px' }}>
          {confirmationTitle}
        </Typography>
        <Typography
          variant={'h5'}
          fontWeight={'500'}
          textAlign={'center'}
          mb={'40px'}
          maxWidth={'800px'}
        >
          {confirmationDescription}
        </Typography>
        <Box
          sx={{
            backgroundColor: '#F7F8FA',
            padding: '16px 16px 0',
            gap: '8px',
            color: theme.palette.primary.main,
            boxShadow: '0px 1px 6px 0px #00000026',
            minWidth: { sm: '100%', lg: '325px' },
          }}
        >
          <Typography variant={'h5'} mb={'16px'}>
            Termindetails
          </Typography>
          <Row>
            <Icon type={'Calendar'} fontSize={'24px'} />
            {bookDate}
          </Row>
          <Row>
            <Icon type={'Clock'} fontSize={'24px'} />
            {bookTime}
          </Row>
          <Row>
            <Icon type={'phone'} fontSize={'22px'} />
            {data.contactForm?.phone}
          </Row>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { sm: '1fr', md: '1fr 1fr' },
          gridGap: { xs: '16px', sm: '16px', md: '32px' },
          width: '100%',
          padding: '16px 32px 32px',
          [theme.breakpoints.down('sm')]: {
            padding: '16px',
          },
        }}
      >
        <Button
          color={'primary'}
          variant={'outlined'}
          fullWidth
          disableElevation
          onClick={closeDialog}
          sx={{ order: { xs: 2, sm: 2, md: 1 } }}
        >
          {closeCtaLabel}
        </Button>
        <Button
          color={'secondary'}
          variant={'contained'}
          fullWidth
          disableElevation
          href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${addToCalendarTitle}&dates=${formattedDate}`}
          target="_blank"
          sx={{ order: { xs: 1, sm: 1, md: 2 } }}
        >
          {addToCalendarCtaLabel}
        </Button>
      </Box>
    </Box>
  );
};
